// AntScore — порт эталонного движка счёта падела на TypeScript (канон).
// Логика 1-в-1 с spec/contract/reference/engine.mjs. Чистый модуль без зависимостей.
//
// Модель undo — реплей: храним только лог победителей очков (pointLog: ('A'|'B')[])
// и каждый раз пересчитываем состояние с нуля. Это гарантирует корректность на границах
// гейма/сета/тайбрейка без хрупкой ручной отмены.

export type Team = 'A' | 'B';

export interface MatchConfig {
  gamesPerSet: number;   // геймов в сете
  setsToWin: number;     // сетов для победы в матче (2 → best-of-3)
  tieBreak: boolean;     // тайбрейк при счёте gamesPerSet-all
  tieBreakPoints: number;// тайбрейк до 7, разрыв 2
  goldenPoint: boolean;  // punto de oro: при ровно решает следующее очко
  advantage: boolean;    // классика «больше/меньше» (игнор. если goldenPoint)
  firstServer: Team;     // кто подаёт первым
}

export interface TeamInfo {
  name: string;
  players: string[];
}

export interface Teams {
  A: TeamInfo;
  B: TeamInfo;
}

export interface Match {
  config: MatchConfig;
  teams: Teams;
  pointLog: Team[];
}

export interface CompletedSet {
  a: number;
  b: number;
  tb?: { a: number; b: number };
}

export interface Snapshot {
  config: MatchConfig;
  teams: Teams;
  pointsA: number;
  pointsB: number;
  displayA: string;
  displayB: string;
  gamesA: number;
  gamesB: number;
  completedSets: CompletedSet[];
  setsWonA: number;
  setsWonB: number;
  server: Team;
  servingSide: 'R' | 'L';
  inTieBreak: boolean;
  tbA: number;
  tbB: number;
  status: 'in_progress' | 'finished';
  winner: Team | null;
  changeEnds: boolean;
  isDeuce: boolean;
  pointLogLength: number;
}

export const POINT_LABELS = ['0', '15', '30', '40'];

export function defaultConfig(): MatchConfig {
  return {
    gamesPerSet: 6,
    setsToWin: 2,
    tieBreak: true,
    tieBreakPoints: 7,
    goldenPoint: false,
    advantage: true,
    firstServer: 'A',
  };
}

export function newMatch(config: Partial<MatchConfig> = {}, teams: Partial<Teams> = {}): Match {
  return {
    config: { ...defaultConfig(), ...config },
    teams: {
      A: { name: teams?.A?.name ?? 'Синие', players: teams?.A?.players ?? [] },
      B: { name: teams?.B?.name ?? 'Красные', players: teams?.B?.players ?? [] },
    },
    pointLog: [],
  };
}

/** Добавить очко команде. Возвращает новый match (иммутабельно). */
export function addPoint(match: Match, team: Team): Match {
  const snap = computeState(match);
  if (snap.status === 'finished') return match;
  return { ...match, pointLog: [...match.pointLog, team] };
}

/** Отменить последнее очко. */
export function undo(match: Match): Match {
  if (match.pointLog.length === 0) return match;
  return { ...match, pointLog: match.pointLog.slice(0, -1) };
}

/** Полный сброс матча (сохраняет config и teams). */
export function reset(match: Match): Match {
  return { ...match, pointLog: [] };
}

// Внутреннее изменяемое состояние при реплее.
interface State {
  config: MatchConfig;
  teams: Teams;
  pointsA: number;
  pointsB: number;
  gamesA: number;
  gamesB: number;
  completedSets: CompletedSet[];
  setsWonA: number;
  setsWonB: number;
  server: Team;
  servingSide: 'R' | 'L';
  inTieBreak: boolean;
  tbA: number;
  tbB: number;
  status: 'in_progress' | 'finished';
  winner: Team | null;
}

/** Пересчёт полного состояния из pointLog. */
export function computeState(match: Match): Snapshot {
  const c = match.config;
  const s: State = {
    config: c,
    teams: match.teams,
    pointsA: 0, pointsB: 0,
    gamesA: 0, gamesB: 0,
    completedSets: [],
    setsWonA: 0, setsWonB: 0,
    server: c.firstServer,
    servingSide: 'R',
    inTieBreak: false,
    tbA: 0, tbB: 0,
    status: 'in_progress',
    winner: null,
  };

  for (const w of match.pointLog) {
    if (s.status === 'finished') break;
    applyPoint(s, w);
  }

  return decorate(s, match);
}

function other(team: Team): Team { return team === 'A' ? 'B' : 'A'; }

function applyPoint(s: State, w: Team): void {
  if (s.inTieBreak) return applyTieBreakPoint(s, w);

  // обычный гейм
  if (w === 'A') s.pointsA++; else s.pointsB++;
  const hi = Math.max(s.pointsA, s.pointsB);
  const lo = Math.min(s.pointsA, s.pointsB);
  let gameWon = false;
  if (hi >= 4) {
    if (s.config.goldenPoint) gameWon = hi > lo;      // при ровно решает одно очко
    else gameWon = (hi - lo) >= 2;                    // больше/меньше
  }
  if (gameWon) {
    finishGame(s, w);
  } else {
    // сторона подачи чередуется каждое очко внутри гейма
    s.servingSide = s.servingSide === 'R' ? 'L' : 'R';
  }
}

function finishGame(s: State, winner: Team): void {
  if (winner === 'A') s.gamesA++; else s.gamesB++;
  s.pointsA = 0; s.pointsB = 0;
  // подающая команда меняется после каждого гейма
  s.server = other(s.server);
  s.servingSide = 'R';
  checkSet(s);
}

function checkSet(s: State): void {
  const gp = s.config.gamesPerSet;
  const gw = Math.max(s.gamesA, s.gamesB);
  const gl = Math.min(s.gamesA, s.gamesB);
  const leader: Team = s.gamesA > s.gamesB ? 'A' : 'B';

  if (gw >= gp && (gw - gl) >= 2) {
    finishSet(s, leader);
    return;
  }
  if (s.config.tieBreak && s.gamesA === gp && s.gamesB === gp) {
    s.inTieBreak = true;
    s.tbA = 0; s.tbB = 0;
    s.servingSide = 'R';
  }
}

function applyTieBreakPoint(s: State, w: Team): void {
  if (w === 'A') s.tbA++; else s.tbB++;
  const need = s.config.tieBreakPoints;
  const hi = Math.max(s.tbA, s.tbB);
  const lo = Math.min(s.tbA, s.tbB);
  if (hi >= need && (hi - lo) >= 2) {
    const winner: Team = s.tbA > s.tbB ? 'A' : 'B';
    // тайбрейк считается как гейм: финальный счёт сета 7-6
    if (winner === 'A') s.gamesA++; else s.gamesB++;
    finishSet(s, winner, { a: s.tbA, b: s.tbB });
    return;
  }
  // ротация подачи в тайбрейке: сторона чередуется каждое очко
  s.servingSide = s.servingSide === 'R' ? 'L' : 'R';
}

function finishSet(s: State, winner: Team, tb?: { a: number; b: number }): void {
  s.completedSets.push({ a: s.gamesA, b: s.gamesB, ...(tb ? { tb } : {}) });
  if (winner === 'A') s.setsWonA++; else s.setsWonB++;
  s.gamesA = 0; s.gamesB = 0;
  s.pointsA = 0; s.pointsB = 0;
  s.inTieBreak = false;
  s.tbA = 0; s.tbB = 0;
  s.servingSide = 'R';
  const need = s.config.setsToWin;
  if (s.setsWonA >= need) { s.status = 'finished'; s.winner = 'A'; }
  else if (s.setsWonB >= need) { s.status = 'finished'; s.winner = 'B'; }
}

/** Человекочитаемые подписи очков текущего гейма/тайбрейка. */
function decorate(s: State, match: Match): Snapshot {
  let displayA: string, displayB: string;
  if (s.inTieBreak) {
    displayA = String(s.tbA);
    displayB = String(s.tbB);
  } else {
    const a = s.pointsA, b = s.pointsB, config = s.config;
    if (!config.goldenPoint && a >= 3 && b >= 3) {
      if (a === b) { displayA = '40'; displayB = '40'; }      // ровно
      else if (a > b) { displayA = 'Ad'; displayB = '—'; }    // больше A
      else { displayA = '—'; displayB = 'Ad'; }               // больше B
    } else {
      displayA = POINT_LABELS[Math.min(a, 3)];
      displayB = POINT_LABELS[Math.min(b, 3)];
    }
  }
  // Смена сторон корта: обычная игра — после нечётного гейма (нечётная сумма геймов);
  // тай-брейк — после каждых 6 очков (3:3, 6:6, 9:9…).
  const totalGames = s.gamesA + s.gamesB;
  const gamesSwap = (totalGames % 2) === 1;
  const tbSwap = s.inTieBreak && Math.floor((s.tbA + s.tbB) / 6) % 2 === 1;
  const changeEnds = gamesSwap !== tbSwap;
  return {
    ...s,
    displayA, displayB,
    changeEnds,
    pointLogLength: match.pointLog.length,
    isDeuce: !s.inTieBreak && !s.config.goldenPoint && s.pointsA >= 3 && s.pointsB >= 3 && s.pointsA === s.pointsB,
  };
}

/** Сериализация для отправки на backend / синка с часами. */
export function serialize(match: Match) {
  return { config: match.config, teams: match.teams, pointLog: match.pointLog };
}

export function deserialize(obj: any): Match {
  return {
    config: { ...defaultConfig(), ...(obj.config || {}) },
    teams: obj.teams ?? { A: { name: 'Синие', players: [] }, B: { name: 'Красные', players: [] } },
    pointLog: obj.pointLog || [],
  };
}
